import React, { useState, useEffect } from "react";
import {
  CreateMCQQuestionContainer,
} from "@sc/McqEditor.styled";
import {
  FreadomPointsInputField,
  ActivityModalContainer,
  ActivityPopUpContainer,
} from "@sc/ActivityModal.styled";
import { ThemeButton, ErrorField, PanelHeader } from "@sc/custom.styled";
import { connectStore } from "redux-box";
import { module as quiz2 } from "@st/quiz2";
import { module as activity } from "@st/activity";
import { module as onboard } from "@st/onboard";
import TextArea from "react-autosize-textarea";
import swal from "sweetalert2";
import { uploadFile } from "@s/api/file";
import "./style_subjective.css";

const errorHandler = (err) => {
  swal({
    text: err,
    type: "warning",
  });
};

const QUESTION_TYPES = {
  TEXT: "text",
  AUDIO: "audio",
  IMAGE: "image",
};

const SubjectiveQuizEditor = (props) => {
  const [question, setQuestion] = useState("");
  const [questionType, setQuestionType] = useState(QUESTION_TYPES.TEXT);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [error, setError] = useState("");
  const [isCreating, setIsCreating] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  const { quiz2, activity } = props;

  // Load existing data if editing
  useEffect(() => {
    if (quiz2 && quiz2.active_mcq) {
      normalizeForState(quiz2.active_mcq);
      setIsCreating(false);
    } else {
      resetForm();
      setIsCreating(true);
    }
  }, [quiz2.active_mcq]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      quiz2.setActiveQuizMcq(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizeForState = (mcq) => {
    if (mcq) {
      setQuestion(mcq.question || (mcq.options && mcq.options.text) || "");
      setQuestionType(
        (mcq.options && mcq.options.type) || QUESTION_TYPES.TEXT
      );
      setCorrectAnswer((mcq.options && mcq.options.correct_answer) || "");

      // Set media preview if available
      if (mcq.options && mcq.options.file_url) {
        setMediaPreview(mcq.options.file_url);
      } else {
        setMediaPreview(null);
        setMediaFile(null);
      }
    }
  };

  const resetForm = () => {
    setQuestion("");
    setQuestionType(QUESTION_TYPES.TEXT);
    setMediaFile(null);
    setMediaPreview(null);
    setCorrectAnswer("");
    setError("");
  };

  const handleMediaFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type based on question type
      const isValidAudio =
        questionType === QUESTION_TYPES.AUDIO &&
        file.type.startsWith("audio/");
      const isValidImage =
        questionType === QUESTION_TYPES.IMAGE &&
        file.type.startsWith("image/");

      if (isValidAudio || isValidImage) {
        setIsUploading(true);
        try {
          // Immediate upload
          const uploadResponse = await uploadFile(file);
          const mediaUrl = uploadResponse.file || uploadResponse.data.url || uploadResponse.data;

          setMediaFile(file);
          setMediaPreview(mediaUrl);
        } catch (uploadError) {
          errorHandler("Failed to upload media file");
          console.error(uploadError);
        } finally {
          setIsUploading(false);
        }
      } else {
        errorHandler(
          `Please select a valid ${questionType} file for this question type.`
        );
      }
    }
  };

  const handleQuestionTypeChange = (e) => {
    const newType = e.target.value;
    setQuestionType(newType);
    // Clear media when switching types
    setMediaFile(null);
    setMediaPreview(null);
  };

  const validateForm = () => {
    if (!question.trim()) {
      setError("* Enter question");
      return false;
    }

    if (
      questionType !== QUESTION_TYPES.TEXT &&
      !mediaPreview
    ) {
      setError(`* Please upload a ${questionType} file`);
      return false;
    }

    if (!correctAnswer.trim()) {
      setError("* Enter correct answer");
      return false;
    }

    setError("");
    return true;
  };

  const handleSaveQuestion = async () => {
    if (!validateForm()) {
      return;
    }

    if (isUploading) {
      errorHandler("Please wait for the file to finish uploading.");
      return;
    }

    try {
      const mcqData = {
        quiz: quiz2.active_quiz.id,
        question: question.trim(),
        options: {
          type: questionType,
          text: question.trim(),
          file_url: mediaPreview,
          correct_answer: correctAnswer.trim(),
        },
      };

      // Add ID if updating existing question
      if (quiz2.active_mcq && quiz2.active_mcq.id) {
        mcqData.id = quiz2.active_mcq.id;
        quiz2.requestUpdateQuizQuestion(mcqData);
      } else {
        quiz2.requestCreateQuizQuestion(mcqData);
      }

      activity.closeEditor();
    } catch (err) {
      errorHandler("Error saving question");
      console.error(err);
    }
  };

  const handleDeleteMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
  };

  return (
    <ActivityPopUpContainer style={{ height: "100%" }}>
      <PanelHeader primary style={{ zIndex: "999" }}>
        <span>{isCreating ? "Create" : "Edit"} QuizQuestion</span>
        {quiz2.active_quiz && (
          <ThemeButton primary onClick={handleSaveQuestion}>
            {isCreating ? "Done" : "Update"}
          </ThemeButton>
        )}
      </PanelHeader>
      <ActivityModalContainer className="subjective_body">
        <ErrorField width={error ? "auto" : "0"}>
          <div style={{ marginTop: 15 }}>{error}</div>
        </ErrorField>

        {/* 1. First dropdown to select type of question */}
        <div style={{ marginBottom: "15px", marginTop: "10px" }}>
          <span>Select Question Type</span>
          <select
            onChange={handleQuestionTypeChange}
            value={questionType}
          >
            <option value={QUESTION_TYPES.TEXT}>Text Question</option>
            <option value={QUESTION_TYPES.AUDIO}>Audio Question</option>
            <option value={QUESTION_TYPES.IMAGE}>Image Question</option>
          </select>
        </div>

        {/* Question Text */}
        <div style={{ marginBottom: "15px" }}>
          <span>Question Text</span>
          <CreateMCQQuestionContainer>
            <TextArea
              placeholder="Enter question"
              autoFocus={true}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
          </CreateMCQQuestionContainer>
        </div>

        {/* 2. Based on type - show UI (Media Upload) */}
        {questionType !== QUESTION_TYPES.TEXT && (
          <div style={{ marginBottom: "15px" }}>
            <span>Upload {questionType.charAt(0).toUpperCase() + questionType.slice(1)}</span>
            <div className="media-upload-area">
              {isUploading ? (
                <div style={{ padding: "20px" }}>
                  <p style={{ marginTop: "10px" }}>Uploading...</p>
                </div>
              ) : mediaPreview ? (
                <div>
                  <div className="media-preview">
                    {questionType === QUESTION_TYPES.IMAGE && (
                      <img
                        src={mediaPreview}
                        alt="Preview"
                      />
                    )}
                    {questionType === QUESTION_TYPES.AUDIO && (
                      <audio
                        controls
                        src={mediaPreview}
                      />
                    )}
                  </div>
                  <button
                    className="remove-media-btn"
                    onClick={handleDeleteMedia}
                  >
                    Remove File
                  </button>
                </div>
              ) : (
                <div>
                  <label className="media-upload-label">
                    <input
                      type="file"
                      accept={
                        questionType === QUESTION_TYPES.AUDIO
                          ? "audio/*"
                          : "image/*"
                      }
                      onChange={handleMediaFileChange}
                      style={{ display: "none" }}
                    />
                    <span>Click to upload</span> or drag and drop
                  </label>
                  <p className="media-upload-hint">
                    {questionType === QUESTION_TYPES.AUDIO
                      ? "MP3, WAV, or OGG files"
                      : "PNG, JPG, or GIF files"}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 4. Correct answer field */}
        <div style={{ marginBottom: "15px" }}>
          <span>Correct Answer</span>
          {/* <FreadomPointsInputField width={"20px"}> */}
            <TextArea
              placeholder="Enter the correct answer"
              value={correctAnswer}
              onChange={(e) => setCorrectAnswer(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 15px",
                borderRadius: "25px",
                border: "1px solid rgba(0, 0, 0, 0.15)",
                fontSize: "var(--sm-font-size)",
                outline: "none",
                backgroundColor: "#fff",
              }}
            />
          {/* </FreadomPointsInputField> */}
        </div>
      </ActivityModalContainer>
    </ActivityPopUpContainer>
  );
};

export default connectStore({ quiz2, activity, onboard })(SubjectiveQuizEditor);
